import axios from '../utils/axios';

// Barcode data interfaces
export interface BarcodeInfo {
  accountId: string;
  objectId: string;
  sku: string;
  itemName: string;
  itemCode?: string;
  hsn?: string;
  mrp?: number;
  salePrice?: number;
  barCodeImage?: string;
  generatedAt?: Date;
  isActive?: boolean;
}

export interface BarcodeGenerationOptions {
  format?: 'CODE128' | 'EAN13' | 'QR';
  width?: number;
  height?: number;
  includeText?: boolean;
  fontSize?: number;
}



class BarcodeService {
  
  // POST Endpoints (Generation)
  
  /**
   * Manual barcode generation for specific SKU
   * POST /api/v1/sku/:id/generate-barcode
   */
  async generateBarcode(
    skuId: string, 
    options: BarcodeGenerationOptions = {}
  ): Promise<BarcodeInfo> {
    try {
      const response = await axios.post(`/api/v1/sku/${skuId}/generate-barcode`, {
        options: {
          format: 'CODE128',
          width: 200,
          height: 60,
          includeText: true,
          fontSize: 12,
          ...options
        }
      });
      
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to generate barcode: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Bulk generation for all SKUs
   * POST /api/v1/sku/auto-generate-all-barcodes
   */
  async autoGenerateAllBarcodes(
    accountId?: string,
    options: BarcodeGenerationOptions = {}
  ): Promise<any> {
    try {
      const response = await axios.post('/api/v1/sku/auto-generate-all-barcodes', {
        accountId,
        options: {
          format: 'CODE128',
          width: 200,
          height: 60,
          includeText: true,
          fontSize: 12,
          ...options
        }
      });
      
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to bulk generate barcodes: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Parse scanned barcode data
   * POST /api/v1/sku/parse-barcode
   */
  async parseBarcode(barcodeData: string | File): Promise<any> {
    try {
      const formData = new FormData();
      
      if (typeof barcodeData === 'string') {
        formData.append('barcodeData', barcodeData);
      } else {
        formData.append('barcodeImage', barcodeData);
      }

      const response = await axios.post('/api/v1/sku/parse-barcode', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to parse barcode: ${error.response?.data?.message || error.message}`);
    }
  }

  // GET Endpoints (Retrieval)

  /**
   * Get barcode image using custom route structure
   * GET /api/v1/sku/:accountId/:objectId/barcode.png
   */
  getBarcodeImageUrl(accountId: string, objectId: string): string {
    return `/api/v1/sku/${accountId}/${objectId}/barcode.png`;
  }

  /**
   * Get SKU data with auto-generated barcode
   * GET /api/v1/sku/:id/with-barcode
   */
  async getSkuWithBarcode(skuId: string): Promise<any> {
    try {
      const response = await axios.get(`/api/v1/sku/${skuId}/with-barcode`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get SKU with barcode: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get optimized scan data for SKU
   * GET /api/v1/sku/:id/scan-data
   */
  async getScanData(skuId: string): Promise<any> {
    try {
      const response = await axios.get(`/api/v1/sku/${skuId}/scan-data`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get scan data: ${error.response?.data?.message || error.message}`);
    }
  }

  // Utility Methods

  /**
   * Download barcode image as file
   */
  async downloadBarcodeImage(
    accountId: string, 
    objectId: string, 
    filename?: string
  ): Promise<void> {
    try {
      const imageUrl = this.getBarcodeImageUrl(accountId, objectId);
      
      // Check if the image exists before attempting download
      const response = await axios.get(imageUrl, {
        responseType: 'blob',
        timeout: 10000 // 10 second timeout
      });

      // Verify we got an actual image
      if (response.data.size === 0) {
        throw new Error('Empty barcode image received');
      }

      // Create download link
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `barcode_${objectId}.png`;
      
      // Ensure the link is added to DOM for some browsers
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Download timed out. Please check your connection and try again.');
      }
      if (error.response?.status === 404) {
        throw new Error('Barcode image not found. Please generate a barcode first.');
      }
      throw new Error(`Failed to download barcode: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Print barcode image
   */
  async printBarcodeImage(accountId: string, objectId: string): Promise<void> {
    try {
      let imageUrl = this.getBarcodeImageUrl(accountId, objectId);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Unable to open print window. Please check popup settings.');
      }

      // Create the print HTML with error handling
      const printHTML = `
        <html>
          <head>
            <title>Print Barcode</title>
            <style>
              body { 
                margin: 0; 
                padding: 20px; 
                display: flex; 
                justify-content: center; 
                align-items: center; 
                min-height: 100vh;
                font-family: Arial, sans-serif;
              }
              img { 
                max-width: 100%; 
                height: auto; 
                border: 1px solid #ddd;
                padding: 10px;
              }
              .error {
                color: red;
                text-align: center;
                padding: 20px;
              }
              @media print {
                body { padding: 0; }
                img { border: none; padding: 0; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <div id="content">
              <img 
                src="${imageUrl}" 
                alt="Barcode" 
                onload="window.print();" 
                onerror="document.getElementById('content').innerHTML='<div class=error>Failed to load barcode image. Please ensure the barcode exists.</div>';"
              />
            </div>
            <script>
              // Auto-close after a delay if print was successful
              setTimeout(() => {
                if (window.confirm('Close print window?')) {
                  window.close();
                }
              }, 2000);
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.write(printHTML);
      printWindow.document.close();
    } catch (error: any) {
      throw new Error(`Failed to print barcode: ${error.message}`);
    }
  }


}

const barcodeService = new BarcodeService();
export default barcodeService;